import * as ko from 'knockout'
import * as _ from 'underscore'

import { ComplexControl } from 'Core/Controls/ComplexControl/ComplexControl'
import { IControlParam } from 'Core/Screens/IScreen'
import { TemplateDesignerStore, IEditTemplateDto, ISaveTemplateDto, IGetLanguagesDto, ICreateTemplateDto, IPreviewHtmlTemplateDto } from 'Core/Controls/TemplateDesigner/Stores/TemplateDesignerStore';
import { IControlValue } from 'Core/Controls/BaseControl/BaseControl';
import { RequiredFieldModel } from 'Core/Controls/ComplexControl/Models/RequiredFieldModel';
import { Notifier } from 'Core/Common/Notifier';
import { RenderModes, FIELD_TYPES, TABLE_TYPES } from 'Core/Constant'
import {EmailSubject, QueryToolbar, Script, Variable} from 'Core/Controls/TemplateDesigner/QueryToolbar/QueryToolbar';
import { EVENTS as QEUERY_TOOLBAR_EVENTS } from 'Core/Controls/TemplateDesigner/QueryToolbar/Events';
import { NOTIFICATIONS, LABELS } from "Core/Components/Translation/Locales";
import { ToolbarQueryEntityJoin } from 'Desktop/Toolbar/ToolbarQueryEntityJoin/ToolbarQueryEntityJoin';
import { TemplateLanguageModel } from 'Core/Controls/TemplateDesigner/Models/TemplateLanguageModel';
import { EditTemplateModel } from 'Core/Controls/TemplateDesigner/Models/EditTemplateModel';
import { BlockUI } from 'Core/Common/BlockUi';
import { SearchScreen } from 'Core/Screens/SearchScreen/SearchScreen';
import { Modal } from 'Core/Common/Modal';
import { CollaboraFrame } from 'Core/Controls/TemplateDesigner/CollaboraFrame';

import ViewTemplate from 'Core/Controls/TemplateDesigner/Templates/View.html';
import ToolBarTemplate from 'Core/Controls/TemplateDesigner/Templates/ToolBar.html';
import FieldVariableTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/Field.html';
import TableVariableTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/Table.html';
import EmailSubjectTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/EmailSubject.html';
import OpenRecordLinkTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/OpenRecordLink.html';

import PlanAppointmentCommandTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/PlanAppointmentCommand.html';
import LoginURLCommandTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/LoginURLCommand.html';
import NextStatusURLCommandTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/NextStatusURLCommand.html';
import StepsNextStatusURLCommandTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/StepsNextStatusURLCommand.html';
import ConfirmedOptinURLCommandTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/ConfirmedOptinURLCommand.html';
import UnsubscribeMeURLCommandTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/UnsubscribeMeURLCommand.html';
import MollieButtonTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/MollieButton.html';
import { MollieButtonEditor } from 'Core/Controls/TemplateDesigner/MollieButtonEditor/MollieButtonEditor';
import ScriptTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/Script.html';
import VariableTemplate from 'Core/Controls/TemplateDesigner/Templates/Variables/Variable.html';
import {GlobalManager, GLOBALS} from 'Core/GlobalManager/GlobalManager';

ko.templates['Core/Controls/TemplateDesigner/Templates/ToolBar'] = ToolBarTemplate;
ko.templates['Core/Controls/TemplateDesigner/Templates/View'] = ViewTemplate;
ko.templates['Core/Controls/TemplateDesigner/Templates/Design'] = ComplexControl.designTemplate;
ko.templates['Core/Controls/TemplateDesigner/Templates/Edit'] = ViewTemplate;
ko.templates['Core/Controls/TemplateDesigner/Templates/NextStatusURLCommand'] = NextStatusURLCommandTemplate;
ko.templates['Core/Controls/TemplateDesigner/Templates/Variables/EmailSubject'] = EmailSubjectTemplate;

const HTML_TEMPLATE = 'html';
/** Word and Excel templates are edited in Collabora, embedded beside the same toolbar the HTML designer uses. */
const DOCUMENT_TEMPLATES = ['docx', 'xlsx'];

const TINYMCE_COMMANDS = {
	INSERT_CONTENT: 'mceInsertContent'
}

const URL_COMMANDS = {
	STEPS_NEXT_STATUS: 'StepsNextStatus',
	NEXT_STATUS: 'NextStatus',
	UNSUBSCRIBE_ME : 'UnsubscribeMe',
	CONFIRMED_OPTIN : 'ConfirmedOptin',
	LOGIN_GUEST: 'LoginGuest',
	LOGIN_GDPR: 'LoginGDPR',
	LOGIN_CONTACT: 'LoginContactUser',
	PLAN_APPOINTMENT: 'PlanAppointment'
}

declare function unescape(s:string): string;
declare function escape(s:string): string;

export class TemplateDesigner extends ComplexControl {
	private _enableEditButton: KnockoutObservable<boolean>;
	private _isWebTemplate: KnockoutObservable<boolean>;
	private _isDocumentTemplate: KnockoutObservable<boolean>;
	private _collaboraSrc: KnockoutObservable<string>;
	private _collaboraToken: KnockoutObservable<string>;
	private _collaboraFileId: string;
	private _collaboraFrame: CollaboraFrame;
	private _toolBar: KnockoutObservable<QueryToolbar>;
	private _tinyEditor: any;
	private _htmlTemplate: string;
	private _fileName: string;
	private _templateFileId: number;
	private _templateFileKSeq: number;
	private _templateLanguages: KnockoutObservableArray<TemplateLanguageModel>;
	private _hasData: KnockoutObservable<boolean>;
	/** The designer sits in whatever column the template screen gives it, which is half a screen at best - too little for a word processor. */
	private _isFullScreen: KnockoutObservable<boolean>;

	constructor(params: IControlParam) {
		super(params);
		this._isWebTemplate = ko.observable(false);
		this._isDocumentTemplate = ko.observable(false);
		this._collaboraSrc = ko.observable('');
		this._collaboraToken = ko.observable('');
		this._toolBar = ko.observable(null);
		this._templateLanguages = ko.observableArray([]);
		this._hasData = ko.observable(false);
		this._isFullScreen = ko.observable(false);
		this.Init();
	}

	ApplyProperties() {}

	/** The editor lays itself out against the size of its frame, and neither it nor TinyMCE is told when that changes - so say it plainly once the new size has been painted, or the document keeps the width it opened at. */
	ToggleFullScreen() {
		const isFullScreen = !this._isFullScreen();
		this._isFullScreen(isFullScreen);

		/** Escape is what a full-screen anything answers to, and the editor itself never sees the key - it is in a frame of its own. */
		if (isFullScreen) {
			document.addEventListener('keydown', this._onFullScreenKey);
		} else {
			document.removeEventListener('keydown', this._onFullScreenKey);
		}

		document.body.classList.toggle('template-designer-open', isFullScreen);

		requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));

		this._collaboraFrame?.Relayout();
	}

	Init() {
		this._enableEditButton = ko.observable(false);
		if (this.IsRunTime) {
			if (this._form && this._form.GetScreen()) {
				const templateType = this._form.GetScreen().GetTableTypeName().replace('.', '').toLowerCase();
				if (templateType === HTML_TEMPLATE) {
					this._isWebTemplate(true);
				} else if (DOCUMENT_TEMPLATES.indexOf(templateType) >= 0) {
					this._isDocumentTemplate(true);
				}
			}
		}

		this._requiredFields([
			new RequiredFieldModel('NAME', FIELD_TYPES.Text, TABLE_TYPES.Entity, null),
			new RequiredFieldModel('F_TYPE', FIELD_TYPES.Lookup, TABLE_TYPES.Entity, null),
			new RequiredFieldModel('TEMPLATE', FIELD_TYPES.Document, TABLE_TYPES.Entity, null)
		]);

		this.InitRequiredFields();

		if (this._isWebTemplate() || this._isDocumentTemplate()) {
			this._toolBar(new QueryToolbar());
			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.DROP_FIELD,
				this,
				(eventArgs) => {
					if (eventArgs.data) {
						if (eventArgs.data.Field && eventArgs.data.Entity) {
							var entityName = eventArgs.data.Entity.Model.Metadata.Name;
							if (eventArgs.data.Entity.Model.Index > 1) {
								entityName = `${entityName}${eventArgs.data.Entity.Model.Index}`;
							}

							var variable = this.GetFieldTemplate(entityName, eventArgs.data.Field.Model.Metadata.Name, eventArgs.data.Field.Model.Metadata.NameTranslation);
							if (this.HasEditor()) {
								this.InsertIntoEditor(variable);
							}
						}
					}
				});

			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.DROP_ENTITY,
				this,
				(eventArgs) => {
					if (eventArgs.data) {
						if (eventArgs.data.Entity) {
							var table = this.GetTableTemplate(eventArgs.data.Entity);
							if (this.HasEditor()) {
								this.InsertIntoEditor(table);
							}
						}
					}
				});

			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.DROP_SPHEERES_LINK,
				this,
				(eventArgs) => {
					if (this.HasEditor()) {
						this.InsertIntoEditor(this.GetSpheeresLinkTemplate());
					}
				});

			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.DROP_OPEN_RECORD_LINK,
					this,
					(eventArgs) => {
						if (this.HasEditor()) {
							this.InsertIntoEditor(this.GetOpenRecordLinkTemplate());
						}
					});

			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.DROP_EMAIL_SUBJECT,
				this,
				(eventArgs) => {
					if (this.HasEditor()) {
						this.InsertIntoEditor(this.GetEmailSubjectTemplate(eventArgs.data.EmailSubject));
					}
				});

			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.DROP_CC,
				this,
				(eventArgs) => {
					if (this.HasEditor()) {
						this.InsertIntoEditor(this.GetEmailSubjectTemplate(eventArgs.data.EmailSubject));
					}
				});

			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.DROP_TO,
				this,
				(eventArgs) => {
					if (this.HasEditor()) {
						this.InsertIntoEditor(this.GetEmailSubjectTemplate(eventArgs.data.EmailSubject));
					}
				});
	
			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.DROP_BCC,
				this,
				(eventArgs) => {
					if (this.HasEditor()) {
							this.InsertIntoEditor(this.GetEmailSubjectTemplate(eventArgs.data.EmailSubject));
						}
			});

			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.DROP_SCRIPT,
				this,
				(eventArgs) => {
					if (this.HasEditor()) {
						if (this._tinyEditor && eventArgs.data.Script.Title === 'MOLLIE BUTTON') {
							this.InsertIntoEditor(this.GetMollieButtonTemplate());
							const inserted = this._tinyEditor.dom.select('a[data-mollie-btn]');
							if (inserted.length > 0) {
								const btn = inserted[inserted.length - 1];
								btn.style.setProperty('color', '#ffffff', 'important');
								new MollieButtonEditor(btn, this._tinyEditor);
							}
						} else {
							this.InsertIntoEditor(this.GetScriptTemplate(eventArgs.data.Script));
						}
					}
			});

			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.DROP_VARIABLE,
				this,
				(eventArgs) => {
					if (this.HasEditor()) {
							this.InsertIntoEditor(this.GetVariableTemplate(eventArgs.data.Variable));
						}
			});

			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.DROP_URL_COMMAND,
				this,
				(eventArgs) => {
					if (this.HasEditor()) {
						this.InsertIntoEditor(this.GetUrlCommandTemplate(eventArgs.data.Id, eventArgs.data.Name, eventArgs.data.Type));

					}
				});

			this._toolBar().On(QEUERY_TOOLBAR_EVENTS.SAVE,
				this,
				(eventArgs) => {
					this.SaveTemplate();
				});

				this._toolBar().On(QEUERY_TOOLBAR_EVENTS.PREVIEW,
					this,
					(eventArgs) => {
						this.Preview(eventArgs.data.EntityId);
					});
		}
	}

	/** The OpenDocumentsInBrowser global is user defined: each user says whether a
	 *  document opens in the browser editor or goes to their desktop client. */
	private get OpenInBrowser(): boolean {
		return GlobalManager.Instance.GetGlobal(GLOBALS.OPEN_DOCUMENTS_IN_BROWSER) === '1';
	}

	private Preview(entityId: number){
		const searchScreen = new SearchScreen({
			ButtonAdd: false,
			EntityId: entityId,
			SearchTerm: '',
			ControlId: 0,
			ConditionToggler: false
		});

		searchScreen.On('RECORD_SELECTED', this, (evtArgs) => {
			/** A document template has no TinyMCE to read the body from - the merge happens server-side and comes back as a read-only editor session.
			 *  Choosing the desktop client falls through to the path this took before that
			 *  existed, so the setting means the same thing here as it does for documents. */
			if (this._isDocumentTemplate() && this.OpenInBrowser) {
				this.PreviewDocument(evtArgs.data.RecordId);
				return;
			}

			BlockUI.Block();
			TemplateDesignerStore
				.Preview({ RecordId: evtArgs.data.RecordId, QueryId: this._toolBar().QueryId, Data: this.ToBase64(this._tinyEditor.getContent()) })
				.always(() => {
					BlockUI.Unblock();
				})
				.then((data) => {
					let modal = new Modal();
					modal.SetContent(this.FromBase64(data));
					modal.Show();
				})
				.fail((err) => {
					var notifier = new Notifier(null);
					notifier.Failed(err.message);
				});

		});

		searchScreen.Show();
	}

	/** The template merged with one record, shown read-only in Collabora. Read-only is the point: these are one record's values, and saving them would overwrite the template with them. */
	private PreviewDocument(recordId: number): void {
		BlockUI.Block();

		TemplateDesignerStore
			.PreviewInEditor({
				ControlId: this.GetControlId(),
				SubjectRecordId: this._form.GetScreen().GetRecordId(),
				KSeq: this._templateFileKSeq,
				FileName: this._fileName,
				Extension: this.TemplateExtension(),
				RecordId: recordId,
				QueryId: this._toolBar().QueryId
			})
			.always(() => {
				BlockUI.Unblock();
			})
			.then((config) => {
				/** The preview is one frame and nothing else, so the dialog is chrome around
				 *  it rather than a window it sits inside - it takes the screen. Height comes
				 *  from the content: fixing both left the dialog taller than the frame and a
				 *  dead strip along the bottom. */
				/** isUseGlobal=false is what makes widthInPercent count. Left at its default
				 *  the Modal reads the PopupWidthInPercent global instead and re-applies it
				 *  inline every 50ms, so the option passed here - and any stylesheet rule -
				 *  is overwritten. That is why 80, then 95, then 100 all looked identical. */
				const modal = new Modal(
					{ widthInPercent: 100, addClass: 'template-designer__preview-modal' },
					false);

				/** The frame is wrapped: a jBox content div has no height of its own, so an
				 *  iframe asking for height:100% collapses to its min-height and leaves the
				 *  rest of the dialog empty. The wrapper carries the height and the frame
				 *  fills it. */
				modal.SetContent(
					`<div class="template-designer__preview">
						<form id="${this.PreviewFormId}" target="${this.PreviewFrameId}" action="${config.iframeSrc}"
						       method="post" enctype="multipart/form-data" style="display:none;">
							<input name="access_token" value="${config.accessToken}" type="hidden" />
							<input name="access_token_ttl" value="0" type="hidden" />
						</form>
						<iframe id="${this.PreviewFrameId}" name="${this.PreviewFrameId}"
						        data-testid="template-designer.collabora-preview"
						        title="Template preview" class="template-designer__preview-frame" allowfullscreen></iframe>
					</div>`
				);
				modal.Show();

				/** Same handshake as the editor: Collabora takes its token by form POST, and the form only exists once the modal has rendered. */
				setTimeout(() => {
					const form = document.getElementById(this.PreviewFormId) as HTMLFormElement;
					if (form) {
						form.submit();
					}
				}, 100);
			})
			.fail((err) => {
				new Notifier(null).Failed(err.message);
			});
	}

	get PreviewFrameId(): string {
		return 'collabora-template-preview-frame';
	}

	get PreviewFormId(): string {
		return 'collabora-template-preview-form';
	}

	private TemplateExtension(): string {
		const name = this._fileName || '';
		const dot = name.lastIndexOf('.');
		return dot >= 0 ? name.substring(dot) : '';
	}

	GetTableTemplate(join: any) {
		var fields = [];
		var titles = [];
		if (join instanceof ToolbarQueryEntityJoin) {
			_.each(join.Entity.Columns, (column) => {
				var entityName = join.Entity.Model.Metadata.Name;
				if (join.Entity.Model.Index > 1) {
					entityName = `${entityName}${join.Entity.Model.Index}`;
				}

				var translation = '';
				if (column.Model.Metadata.NameTranslation) {
					translation = `(${column.Model.Metadata.NameTranslation})`;
				};

				fields.push(`${entityName}.${column.Model.Metadata.Name}${translation}`);
				var title = column.Model.Metadata.NameTranslation ? column.Model.Metadata.NameTranslation : `${join.Entity.Model.Metadata.Name}.${column.Model.Metadata.Name}`;
				titles.push(title);
			});

			if(join.LinkEntity){
				_.each(join.LinkEntity.Columns, (column) => {
					var entityName = join.LinkEntity.Model.Metadata.Name;
					if (join.LinkEntity.Model.Index > 1) {
						entityName = `${entityName}${join.LinkEntity.Model.Index}`;
					}

					var translation = '';
					if (column.Model.Metadata.NameTranslation) {
						translation = `(${column.Model.Metadata.NameTranslation})`;
					};

					fields.push(`${entityName}.${column.Model.Metadata.Name}${translation}`);
					var title = column.Model.Metadata.NameTranslation ? column.Model.Metadata.NameTranslation : `${join.LinkEntity.Model.Metadata.Name}.${column.Model.Metadata.Name}`;
					titles.push(title);
				});
			}
		}

		var variables = [];

		_.each(fields, field => {
			variables.push(`{REPEAT ${field}}`);
		});

		return (ko as any).renderTemplateXHtml(TableVariableTemplate, { Columns: titles, Fields: variables });
	}

	GetFieldTemplate(entityName: string, fieldName: string, fieldNameTranslation: string) {
		var translation = '';
		if (fieldNameTranslation) { translation = `(${fieldNameTranslation})`; }
		return (ko as any).renderTemplateXHtml(FieldVariableTemplate, `{SINGLE ${entityName}.${fieldName}${translation}}`);
	}

	GetEmailSubjectTemplate(subject: EmailSubject) {
		return (ko as any).renderTemplateXHtml(EmailSubjectTemplate, `${subject.Value}`);
	}

	GetScriptTemplate(script: Script) {
		return (ko as any).renderTemplateXHtml(ScriptTemplate, `${script.Value}`);
	}

	GetMollieButtonTemplate() {
		return MollieButtonTemplate;
	}

	GetVariableTemplate(variable: Variable) {
		return (ko as any).renderTemplateXHtml(VariableTemplate, `${variable.Value}`);
	}

	GetSpheeresLinkTemplate() {
		return (ko as any).renderTemplateXHtml(FieldVariableTemplate, '{SPHEERES_LINK}');
	}

	GetOpenRecordLinkTemplate(){
		return OpenRecordLinkTemplate;
	}

	GetUrlCommandTemplate(id: string, name: string, type: string) {

		if(URL_COMMANDS.STEPS_NEXT_STATUS === type){
			return (ko as any).renderTemplateXHtml(StepsNextStatusURLCommandTemplate, { id: id, name: name, type: type });
		}

		if(URL_COMMANDS.NEXT_STATUS === type){
			return (ko as any).renderTemplateXHtml(NextStatusURLCommandTemplate, { id: id, name: name, type: type });
		}

		if(URL_COMMANDS.CONFIRMED_OPTIN === type){
			return (ko as any).renderTemplateXHtml(ConfirmedOptinURLCommandTemplate, { id: id, name: name, type: type });
		}

		if(URL_COMMANDS.UNSUBSCRIBE_ME === type){
			return (ko as any).renderTemplateXHtml(UnsubscribeMeURLCommandTemplate, { id: id, name: name, type: type });
		}
		
		if(URL_COMMANDS.PLAN_APPOINTMENT === type){
			return (ko as any).renderTemplateXHtml(PlanAppointmentCommandTemplate, { id: id, name: name, type: type });
		}

		if(URL_COMMANDS.LOGIN_GUEST === type || URL_COMMANDS.LOGIN_CONTACT === type || URL_COMMANDS.LOGIN_GDPR === type){
			return (ko as any).renderTemplateXHtml(LoginURLCommandTemplate, { id: id, name: name, type: type });
		}

		return '';
	}

	SetValue(value: IControlValue): void {
		if (value && value.SubjectRecordId > 0) {
			this._enableEditButton(true);
			this.LoadData();
		}
	}

	LoadData() {
		let request: IGetLanguagesDto = {
			SubjectEntityId: this.GetForm().GetScreen().GetEntityId(),
			SubjectRecordId: this.GetForm().GetScreen().GetRecordId(),
			ControlId: this.GetControlId()
		};
		
		TemplateDesignerStore.GetLanguages(request).then((languages)=>{
			this._templateLanguages(languages);
		}).fail(err=>{
			new Notifier(null).Failed(err.message);
		});
	}

	UpdateTemplate(template: TemplateLanguageModel) {
		this.SelectTemplateLanguage();

		var requestModel: IEditTemplateDto = {
			SubjectRecordId: this._form.GetScreen().GetRecordId(),
			SubjectEntityId: this._form.GetScreen().GetEntityId(),
			LanguageId: template.Id,
			ControlId: this.GetControlId()
		};

		BlockUI.Block();
		TemplateDesignerStore
			.Edit(requestModel)
			.always(() => {
				BlockUI.Unblock();
			})
			.then((template) => {
				this.SetTemplate(template);
			})
			.fail((err) => {
				var notifier = new Notifier(null);
				notifier.Failed(err.message);
			});
	}

	SetTemplate(template: EditTemplateModel){
		/** A template bound for the desktop client is answered with nothing: the server has already pushed it there over SignalR. */
		if (!template) {
			return;
		}

		if(this._isWebTemplate() || this._isDocumentTemplate()){
			this._toolBar().QueryId = template.QueryId;
			this._fileName = template.FileName;
			this._templateFileId = template.RecordId
			this._templateFileKSeq = template.KSeq;
			this._toolBar().SetCommands(template.UrlCommands);
			this._toolBar().Refresh();
		}

		if(this._isWebTemplate()){
			if (this._tinyEditor) {
				this._tinyEditor.setContent(this.FromBase64(template.Content));
			} else {
				this._htmlTemplate = this.FromBase64(template.Content);
			}
			this._hasData(true);
		}

		if (this._isDocumentTemplate()) {
			this._hasData(true);
			this.OpenInCollabora();
		}
	}

	get CollaboraFrameId(): string {
		return 'collabora-template-frame';
	}

	get CollaboraFormId(): string {
		return 'collabora-template-form';
	}

	private HasEditor(): boolean {
		return !!this._tinyEditor || !!this._collaboraFrame;
	}

	/** The toolbar builds the same field codes either way; only where they land differs. */
	private InsertIntoEditor(content: string): void {
		if (this._tinyEditor) {
			this._tinyEditor.execCommand(TINYMCE_COMMANDS.INSERT_CONTENT, false, content);
			return;
		}

		if (this._collaboraFrame) {
			this._collaboraFrame.PasteHtml(content);
		}
	}

	private OpenInCollabora(): void {
		BlockUI.Block();

		TemplateDesignerStore
			.GetTemplateEditorConfig({
				ControlId: this.GetControlId(),
				SubjectRecordId: this._form.GetScreen().GetRecordId(),
				KSeq: this._templateFileKSeq,
				FileName: this._fileName
			})
			.always(() => {
				BlockUI.Unblock();
			})
			.then((config) => {
				this._collaboraSrc(config.iframeSrc);
				this._collaboraToken(config.accessToken);
				this._collaboraFileId = config.fileId;

				/** Collabora takes its access token by form POST rather than on the query string, so the frame stays empty until the form is submitted - and the form only exists once _hasData has rendered the editor half. */
				setTimeout(() => {
					const form = document.getElementById(this.CollaboraFormId) as HTMLFormElement;
					const frame = document.getElementById(this.CollaboraFrameId) as HTMLIFrameElement;
					if (!form || !frame) {
						return;
					}

					this._collaboraFrame = new CollaboraFrame(frame);
					this._toolBar().SetDropContainer(frame.parentElement);
					form.submit();
				}, 100);
			})
			.fail((err) => {
				new Notifier(null).Failed(err.message);
			});
	}

	/** Collabora writes to its own working copy as the user types; this asks it to flush that copy, then has the server put those bytes through the platform's save, so the file version, its lock and the query link all move together. */
	private SaveDocumentTemplate(): void {
		if (!this._collaboraFrame) {
			return;
		}

		BlockUI.Block();
		this._collaboraFrame
			.Save()
			.then(() => {
				return TemplateDesignerStore.SaveFromEditor({
					FileId: this._collaboraFileId,
					AccessToken: this._collaboraToken(),
					ControlId: this.GetControlId(),
					RecordId: this._templateFileId,
					KSeq: this._templateFileKSeq,
					QueryId: this._toolBar().QueryId,
					FileName: this._fileName
				});
			})
			.always(() => {
				BlockUI.Unblock();
			})
			.then(() => {
				new Notifier(null).Success(NOTIFICATIONS.RECORD_SAVED);
			})
			.fail((err) => {
				new Notifier(null).Failed(err.message);
			});
	}

	SaveTemplate() {
		if (this._isDocumentTemplate()) {
			this.SaveDocumentTemplate();
			return;
		}

		var requestModel: ISaveTemplateDto = {
			FileName: this._fileName,
			Content: this.ToBase64(this._tinyEditor.getContent()),
			QueryId: this._toolBar().QueryId,
			ControlId: this.GetControlId(),
			RecordId: this._templateFileId,
			KSeq: this._templateFileKSeq
		}

		BlockUI.Block();
		TemplateDesignerStore
			.Save(requestModel)
			.always(() => {
				BlockUI.Unblock();
			})
			.then(() => {
				var notifier = new Notifier(null);
				notifier.Success(NOTIFICATIONS.RECORD_SAVED);
			})
			.fail((err) => {
				var notifier = new Notifier(null);
				notifier.Failed(err.message);
			});
	}

	private ToBase64(str: string): string {
		return window.btoa(unescape(encodeURIComponent(str)));
	}
	
	private FromBase64(str: string):string {
		return decodeURIComponent(escape(window.atob(str)));
	}

	SetupTiny(editor) {
		editor.ui.registry.addMenuItem('editMollieButton', {
			text: 'Edit Mollie Button',
			icon: 'edit-block',
			onAction: () => {
				const node = editor.selection.getNode();
				const mollieLink = node.closest('a[href*="{MOLLIE}"]') || node.parentElement?.closest('a[href*="{MOLLIE}"]');
				if (mollieLink) {
					new MollieButtonEditor(mollieLink as HTMLElement, editor);
				}
			}
		});

		editor.ui.registry.addContextMenu('mollie', {
			update: (element) => {
				const mollieLink = element.closest('a[href*="{MOLLIE}"]') || element.parentElement?.closest('a[href*="{MOLLIE}"]');
				return mollieLink ? 'editMollieButton' : '';
			}
		});
	}

	AfterTinyInit(editor) {
		this._tinyEditor = editor;
		if (this._htmlTemplate) {
			this._tinyEditor.setContent(this._htmlTemplate);
		}

		const mollieButtons = editor.dom.select('a[data-mollie-btn]');
		mollieButtons.forEach((btn) => {
			const color = btn.style.color || '#ffffff';
			btn.style.setProperty('color', color, 'important');
		});

		editor.on('dblclick', (e) => {
			const node = e.target.closest('a[href*="{MOLLIE}"]');
			if (node) {
				e.preventDefault();
				new MollieButtonEditor(node, editor);
			}
		});
	}

	SelectTemplateLanguage(){
		this._hasData(false);
		this._tinyEditor = null;
		this.LeaveFullScreen();
	}

	private LeaveFullScreen() {
		this._isFullScreen(false);
		document.body.classList.remove('template-designer-open');
		document.removeEventListener('keydown', this._onFullScreenKey);
	}

	/** Bound once so it can be taken off again; a fresh arrow per call would leave a listener behind on every toggle. */
	private _onFullScreenKey = (event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			this.LeaveFullScreen();
		}
	};

	CreateTemplate(templateLanguage: TemplateLanguageModel){

		var requestModel: ICreateTemplateDto = {
			SubjectRecordId: this._form.GetScreen().GetRecordId(),
			SubjectEntityId: this._form.GetScreen().GetEntityId(),
			LanguageId: templateLanguage.Id,
			ControlId: this.GetControlId()
		};

		BlockUI.Block();
		TemplateDesignerStore
			.Create(requestModel)
			.always(() => {
				BlockUI.Unblock();
				this.LoadData();
			})
			.then((template) => { 
				templateLanguage.HasTemplate = true;
				this._templateLanguages.valueHasMutated();
				this.SetTemplate(template);
			})
			.fail((err) => {
				var notifier = new Notifier(null);
				notifier.Failed(err.message);
			});
	}

	AfterTinyChange() { }
}